/**
 * Frontend validation utilities for profile forms
 */

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

// Country data with phone codes and validation patterns
export const COUNTRIES = [
  { code: 'US', name: 'United States', phoneCode: '+1', phonePattern: /^\+1\s?\(?\d{3}\)?\s?\d{3}-?\d{4}$/ },
  { code: 'CA', name: 'Canada', phoneCode: '+1', phonePattern: /^\+1\s?\(?\d{3}\)?\s?\d{3}-?\d{4}$/ },
  { code: 'GB', name: 'United Kingdom', phoneCode: '+44', phonePattern: /^\+44\s?\d{4}\s?\d{6}$/ },
  { code: 'AU', name: 'Australia', phoneCode: '+61', phonePattern: /^\+61\s?\d{1}\s?\d{4}\s?\d{4}$/ },
  { code: 'DE', name: 'Germany', phoneCode: '+49', phonePattern: /^\+49\s?\d{3,4}\s?\d{7,8}$/ },
  { code: 'FR', name: 'France', phoneCode: '+33', phonePattern: /^\+33\s?\d{1}\s?\d{2}\s?\d{2}\s?\d{2}\s?\d{2}$/ },
  { code: 'IN', name: 'India', phoneCode: '+91', phonePattern: /^\+91\s?\d{5}\s?\d{5}$/ },
  { code: 'JP', name: 'Japan', phoneCode: '+81', phonePattern: /^\+81\s?\d{1,4}\s?\d{4}\s?\d{4}$/ },
  { code: 'CN', name: 'China', phoneCode: '+86', phonePattern: /^\+86\s?\d{3}\s?\d{4}\s?\d{4}$/ },
  { code: 'BR', name: 'Brazil', phoneCode: '+55', phonePattern: /^\+55\s?\d{2}\s?\d{4,5}\s?\d{4}$/ },
  { code: 'MX', name: 'Mexico', phoneCode: '+52', phonePattern: /^\+52\s?\d{3}\s?\d{3}\s?\d{4}$/ },
  { code: 'ES', name: 'Spain', phoneCode: '+34', phonePattern: /^\+34\s?\d{3}\s?\d{3}\s?\d{3}$/ },
  { code: 'IT', name: 'Italy', phoneCode: '+39', phonePattern: /^\+39\s?\d{3}\s?\d{3}\s?\d{4}$/ },
  { code: 'NL', name: 'Netherlands', phoneCode: '+31', phonePattern: /^\+31\s?\d{1}\s?\d{4}\s?\d{4}$/ },
  { code: 'SE', name: 'Sweden', phoneCode: '+46', phonePattern: /^\+46\s?\d{2}\s?\d{3}\s?\d{4}$/ },
  { code: 'NO', name: 'Norway', phoneCode: '+47', phonePattern: /^\+47\s?\d{4}\s?\d{4}$/ },
  { code: 'DK', name: 'Denmark', phoneCode: '+45', phonePattern: /^\+45\s?\d{4}\s?\d{4}$/ },
  { code: 'FI', name: 'Finland', phoneCode: '+358', phonePattern: /^\+358\s?\d{2}\s?\d{3}\s?\d{4}$/ },
  { code: 'CH', name: 'Switzerland', phoneCode: '+41', phonePattern: /^\+41\s?\d{2}\s?\d{3}\s?\d{4}$/ },
  { code: 'AT', name: 'Austria', phoneCode: '+43', phonePattern: /^\+43\s?\d{3}\s?\d{3}\s?\d{4}$/ }
];

// Popular cities by country for location suggestions
export const CITIES_BY_COUNTRY: Record<string, string[]> = {
  'US': ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia', 'San Antonio', 'San Diego', 'Dallas', 'San Jose'],
  'CA': ['Toronto', 'Montreal', 'Vancouver', 'Calgary', 'Edmonton', 'Ottawa', 'Winnipeg', 'Quebec City', 'Hamilton', 'Kitchener'],
  'GB': ['London', 'Birmingham', 'Manchester', 'Glasgow', 'Liverpool', 'Leeds', 'Sheffield', 'Edinburgh', 'Bristol', 'Cardiff'],
  'AU': ['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide', 'Gold Coast', 'Newcastle', 'Canberra', 'Sunshine Coast', 'Wollongong'],
  'DE': ['Berlin', 'Hamburg', 'Munich', 'Cologne', 'Frankfurt', 'Stuttgart', 'Düsseldorf', 'Dortmund', 'Essen', 'Leipzig'],
  'FR': ['Paris', 'Marseille', 'Lyon', 'Toulouse', 'Nice', 'Nantes', 'Strasbourg', 'Montpellier', 'Bordeaux', 'Lille'],
  'IN': ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Kolkata', 'Pune', 'Ahmedabad', 'Jaipur', 'Surat'],
  'JP': ['Tokyo', 'Yokohama', 'Osaka', 'Nagoya', 'Sapporo', 'Fukuoka', 'Kobe', 'Kyoto', 'Kawasaki', 'Saitama'],
  'CN': ['Beijing', 'Shanghai', 'Guangzhou', 'Shenzhen', 'Tianjin', 'Wuhan', 'Dongguan', 'Chengdu', 'Nanjing', 'Chongqing'],
  'BR': ['São Paulo', 'Rio de Janeiro', 'Brasília', 'Salvador', 'Fortaleza', 'Belo Horizonte', 'Manaus', 'Curitiba', 'Recife', 'Goiânia']
};

/**
 * Validate name fields (firstName, lastName, name)
 */
export const validateName = (value: string, fieldName: string, required: boolean = false): ValidationError | null => {
  if (!value || value.trim() === '') {
    if (required) {
      return { field: fieldName, message: `${fieldName} is required` };
    }
    return null;
  }

  if (value.length > 50) {
    return { field: fieldName, message: `${fieldName} must be less than 50 characters` };
  }

  if (!/^[a-zA-Z\s\-'\.]+$/.test(value)) {
    return { field: fieldName, message: `${fieldName} can only contain letters, spaces, hyphens, apostrophes, and periods` };
  }

  return null;
};

/**
 * Validate phone number with country-specific patterns
 */
export const validatePhone = (value: string, countryCode?: string): ValidationError | null => {
  if (!value || value.trim() === '') {
    return null; // Phone is optional
  }

  // Remove all spaces and special characters for basic validation
  const cleanPhone = value.replace(/[\s\-\(\)]/g, '');

  // Basic length check (including country code)
  if (cleanPhone.length < 7 || cleanPhone.length > 20) {
    return { field: 'phone', message: 'Phone number must be between 7-20 digits' };
  }

  // If country code is provided, do more flexible validation
  if (countryCode) {
    const country = COUNTRIES.find(c => c.code === countryCode);
    if (country) {
      // Check if it starts with the country code
      if (!value.startsWith(country.phoneCode)) {
        return { field: 'phone', message: `Phone number should start with ${country.phoneCode} for ${country.name}` };
      }

      // Check if there are enough digits after country code
      const afterCountryCode = value.replace(country.phoneCode, '').replace(/[\s\-\(\)]/g, '');
      if (afterCountryCode.length < 6 || afterCountryCode.length > 15) {
        return { field: 'phone', message: `Please enter a valid ${country.name} phone number` };
      }
    }
  } else {
    // Generic phone validation
    if (!/^[\+]?[\d\s\-\(\)]{7,20}$/.test(value)) {
      return { field: 'phone', message: 'Phone number can only contain numbers, spaces, dashes, parentheses, and plus sign' };
    }
  }

  return null;
};

/**
 * Validate location field
 */
export const validateLocation = (value: string): ValidationError | null => {
  if (!value || value.trim() === '') {
    return null; // Location is optional
  }

  if (value.length > 100) {
    return { field: 'location', message: 'Location must be less than 100 characters' };
  }

  // Allow letters, numbers, spaces, commas, periods, hyphens, apostrophes
  if (!/^[a-zA-Z0-9\s\,\.\-']+$/.test(value)) {
    return { field: 'location', message: 'Location can only contain letters, numbers, spaces, commas, periods, hyphens, and apostrophes' };
  }

  return null;
};

/**
 * Validate birthday field
 */
export const validateBirthday = (value: string): ValidationError | null => {
  if (!value || value.trim() === '') {
    return null; // Birthday is optional
  }

  if (value.length > 50) {
    return { field: 'birthday', message: 'Birthday must be less than 50 characters' };
  }

  // Try to parse as date if it looks like a date
  const datePattern = /^\d{1,2}\/\d{1,2}\/\d{4}$|^\d{4}-\d{1,2}-\d{1,2}$/;
  if (datePattern.test(value)) {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return { field: 'birthday', message: 'Please enter a valid date' };
    }

    // Check if date is in the future
    if (date > new Date()) {
      return { field: 'birthday', message: 'Birthday cannot be in the future' };
    }

    // Check if date is too far in the past (more than 150 years)
    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - 150);
    if (date < minDate) {
      return { field: 'birthday', message: 'Please enter a valid birth year' };
    }
  }

  return null;
};

/**
 * Validate bio field
 */
export const validateBio = (value: string): ValidationError | null => {
  if (!value || value.trim() === '') {
    return null; // Bio is optional
  }

  if (value.length > 500) {
    return { field: 'bio', message: 'Bio must be less than 500 characters' };
  }

  return null;
};

/**
 * Validate password
 */
export const validatePassword = (value: string): ValidationError | null => {
  if (!value || value.trim() === '') {
    return { field: 'password', message: 'Password is required' };
  }

  if (value.length < 8) {
    return { field: 'password', message: 'Password must be at least 8 characters long' };
  }

  if (value.length > 128) {
    return { field: 'password', message: 'Password must be less than 128 characters' };
  }

  // Check for required character types
  const hasUppercase = /[A-Z]/.test(value);
  const hasLowercase = /[a-z]/.test(value);
  const hasNumber = /\d/.test(value);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);

  if (!hasUppercase) {
    return { field: 'password', message: 'Password must contain at least one uppercase letter' };
  }

  if (!hasLowercase) {
    return { field: 'password', message: 'Password must contain at least one lowercase letter' };
  }

  if (!hasNumber) {
    return { field: 'password', message: 'Password must contain at least one number' };
  }

  if (!hasSpecialChar) {
    return { field: 'password', message: 'Password must contain at least one special character' };
  }

  return null;
};

/**
 * Validate profile form data
 */
export const validateProfileForm = (data: any, countryCode?: string): ValidationResult => {
  const errors: ValidationError[] = [];

  // Validate each field
  const nameError = validateName(data.firstName, 'First name');
  if (nameError) errors.push(nameError);

  const lastNameError = validateName(data.lastName, 'Last name');
  if (lastNameError) errors.push(lastNameError);

  const phoneError = validatePhone(data.phone, countryCode);
  if (phoneError) errors.push(phoneError);

  const locationError = validateLocation(data.location);
  if (locationError) errors.push(locationError);

  const birthdayError = validateBirthday(data.birthday);
  if (birthdayError) errors.push(birthdayError);

  const bioError = validateBio(data.bio);
  if (bioError) errors.push(bioError);

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Format phone number based on country
 */
export const formatPhoneNumber = (value: string, countryCode: string): string => {
  const country = COUNTRIES.find(c => c.code === countryCode);
  if (!country) return value;

  // Remove all non-digits except plus
  const cleaned = value.replace(/[^\d+]/g, '');

  // Add country code if not present
  if (!cleaned.startsWith(country.phoneCode)) {
    return country.phoneCode + ' ' + cleaned.replace(/^\+/, '');
  }

  return cleaned;
};

/**
 * Get country by phone code
 */
export const getCountryByPhoneCode = (phoneCode: string): typeof COUNTRIES[0] | null => {
  return COUNTRIES.find(c => c.phoneCode === phoneCode) || null;
};

/**
 * Search countries by name
 */
export const searchCountries = (query: string): typeof COUNTRIES => {
  if (!query) return COUNTRIES;

  const lowerQuery = query.toLowerCase();
  return COUNTRIES.filter(country =>
    country.name.toLowerCase().includes(lowerQuery) ||
    country.code.toLowerCase().includes(lowerQuery)
  );
};
